/**
 * HoverVideoPreview — marketplace tile photo with optional 4-second
 * hover-to-play preview of the dog's first working-video file clip.
 *
 * Design choices:
 *  • Only previews `kind === "file"` videos (self-hosted MP4/MOV/WEBM)
 *    where we control the source. YouTube/Vimeo embeds require iframe
 *    + click-through to play, which defeats the snappy hover UX — and
 *    Apple/Google iframe sandboxing blocks programmatic autoplay
 *    anyway. Tiles with embed-only videos still get a "▶ Video" badge
 *    so the buyer knows to click through.
 *  • Plays MUTED + autoplay so Safari / iOS allow it without user
 *    gesture (Safari's autoplay policy: muted videos may autoplay).
 *  • Auto-stops at 4 seconds OR on `mouseleave`, whichever comes first,
 *    then reverts to the static poster. We don't loop — the buyer's
 *    next interaction should be the click-through, not endless hover.
 *  • Touch-friendly: on devices without hover (mobile/tablet), the
 *    preview never triggers; the tile behaves like a normal photo
 *    card. The "▶ Video" badge still appears so the buyer knows
 *    there's footage waiting after they tap through.
 */
import React, { useEffect, useRef, useState } from "react";
import { PawPrintIcon as PawPrint, VideoCameraIcon as VideoCamera } from "@phosphor-icons/react";

const PREVIEW_SECONDS = 4;

export default function HoverVideoPreview({
  photoUrl,
  videos,
  alt,
  className = "",
  imgClassName = "",
}) {
  const [previewing, setPreviewing] = useState(false);
  const videoRef = useRef(null);
  const stopTimeoutRef = useRef(null);

  // First eligible file-kind clip drives the preview. Embed-only
  // dogs still get the corner badge but no autoplay.
  const previewSrc = React.useMemo(() => {
    const list = Array.isArray(videos) ? videos : [];
    const fileClip = list.find((v) => v && v.kind === "file" && v.url);
    return fileClip?.url || null;
  }, [videos]);
  const hasAnyVideo = Array.isArray(videos) && videos.length > 0;

  const startPreview = () => {
    if (!previewSrc) return;
    setPreviewing(true);
  };

  const stopPreview = () => {
    setPreviewing(false);
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
        v.currentTime = 0;
      } catch { /* ignore */ }
    }
  };

  // When previewing flips on, attempt autoplay and arm the 4s cutoff.
  useEffect(() => {
    if (!previewing) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const playP = v.play();
    if (playP && typeof playP.catch === "function") {
      playP.catch(() => stopPreview()); // Browser refused autoplay; revert.
    }
    stopTimeoutRef.current = setTimeout(stopPreview, PREVIEW_SECONDS * 1000);
    return () => {
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
    };
  }, [previewing]);

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
      data-testid="hover-video-preview"
      data-previewing={previewing ? "1" : "0"}
    >
      {/* Static poster — always rendered, hidden under the video while
          previewing. Keeps the layout from jumping while the video
          decodes its first frame. */}
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={alt || ""}
          className={`w-full h-full object-cover ${imgClassName}`}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full bg-cream flex items-center justify-center">
          <PawPrint size={48} weight="duotone" className="text-gold" />
        </div>
      )}

      {/* The preview video — mounted only when previewing so we don't
          waste bandwidth fetching MP4s for every tile on the page. */}
      {previewing && previewSrc && (
        <video
          ref={videoRef}
          src={previewSrc}
          poster={photoUrl || undefined}
          muted
          autoPlay
          playsInline
          preload="auto"
          className={`absolute inset-0 w-full h-full object-cover ${imgClassName}`}
          aria-hidden="true"
          data-testid="hover-video-preview-video"
        />
      )}

      {/* Tiny "Video" badge so buyers know there's footage even when
          they're on a touch device that doesn't trigger hover. */}
      {hasAnyVideo && (
        <span
          className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-bold uppercase tracking-[0.12em] backdrop-blur-sm"
          data-testid="hover-video-preview-badge"
        >
          <VideoCamera size={11} weight="fill" /> Video
        </span>
      )}
    </div>
  );
}
